import { ChevronRightIcon } from '@radix-ui/react-icons'
import { Link } from '@tanstack/react-router'
import { Award, Lock, ShieldCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { FlickeringGrid } from '@/components/ui/flickering-grid'
import { cn, useMediaQuery } from '@/lib'
import { useTrainings } from '@/features/trainings'

const footerSiteConfigBase = {
  navLinks: [
    { id: 5, key: 'home', to: '/' },
    { id: 6, key: 'programs', to: '/programmes' },
    { id: 7, key: 'catalog', to: '/login' },
    { id: 8, key: 'about', to: '/about' },
    { id: 9, key: 'contact', to: '/contact' },
  ],
  accountLinks: [
    { id: 10, key: 'login', to: '/login' },
    { id: 11, key: 'signup', to: '/signup' },
  ],
} as const



function IconLinkedIn({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  )
}

function IconInstagram({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
    </svg>
  )
}



const socialLinks = [
  { key: 'linkedin', href: 'https://www.linkedin.com/company/skillsup-ai' },
  { key: 'instagram', href: 'https://www.instagram.com/skillsupai.training/' },
] as const

function FooterNavItem({ title, to }: { title: string; to: string }) {
  return (
    <li className="group inline-flex cursor-pointer items-center justify-start gap-1 text-[15px]/snug text-white/60">
      <Link to={to} className="transition-colors hover:text-white">
        {title}
      </Link>
      <div className="flex size-4 translate-x-0 transform items-center justify-center rounded-none border border-white/20 opacity-0 transition-all duration-300 ease-out group-hover:translate-x-1 group-hover:opacity-100">
        <ChevronRightIcon className="h-4 w-4 text-white/80" />
      </div>
    </li>
  )
}

export function FlickeringFooter() {
  const { t } = useTranslation()
  const tablet = useMediaQuery('(max-width: 1024px)')
  const narrow = useMediaQuery('(max-width: 480px)')
  const { data: trainings } = useTrainings()

  return (
    <footer id="footer" className="w-full border-t border-[#4a4455]/60 bg-[#100d16] pb-0">
      <div className="mx-auto flex max-w-[1440px] flex-col gap-10 p-10 md:flex-row md:items-start md:justify-between">
        <div className="mx-0 flex max-w-xs flex-col items-start justify-start gap-y-5">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo_light_mode.png" alt="" className="h-11 w-auto object-contain sm:h-12 md:h-14 dark:hidden" />
            <img src="/skillsuplogo.png" alt="" className="hidden h-11 w-auto object-contain sm:h-12 md:h-14 dark:block" />
          </Link>
          <p className="font-medium tracking-tight text-white/60">{t('footer.description')}</p>
          <div className="flex items-center gap-3 text-white/50" aria-hidden>
            <ShieldCheck className="size-10 stroke-[1.25]" stroke="currentColor" />
            <Award className="size-10 stroke-[1.25]" stroke="currentColor" />
            <Lock className="size-10 stroke-[1.25]" stroke="currentColor" />
          </div>
        </div>
        <div className="w-full pt-2 md:w-1/2">
          <div className="flex flex-col items-start justify-start gap-y-8 md:flex-row md:items-start md:justify-between lg:pl-6">

            {/* Programs — from DB */}
            <ul className="flex flex-col gap-y-2">
              <li className="mb-1 text-sm font-semibold text-[#d2bbff]">{t('footer.trainingsLabel')}</li>
              {trainings && trainings.length > 0 ? (
                trainings.slice(0, 5).map((training) => (
                  <FooterNavItem key={training.id} title={training.title} to="/programmes" />
                ))
              ) : (
                <FooterNavItem title={t('footer.viewAllPrograms')} to="/programmes" />
              )}
            </ul>

            {/* Navigation */}
            <ul className="flex flex-col gap-y-2">
              <li className="mb-1 text-sm font-semibold text-[#d2bbff]">{t('footer.navigationLabel')}</li>
              {footerSiteConfigBase.navLinks.map((link) => (
                <FooterNavItem key={link.id} title={t(`footer.navLinks.${link.key}`)} to={link.to} />
              ))}
            </ul>

            {/* Account */}
            <ul className="flex flex-col gap-y-2">
              <li className="mb-1 text-sm font-semibold text-[#d2bbff]">{t('footer.accountLabel')}</li>
              {footerSiteConfigBase.accountLinks.map((link) => (
                <FooterNavItem key={link.id} title={t(`footer.accountLinks.${link.key}`)} to={link.to} />
              ))}
            </ul>

          </div>
        </div>
      </div>

      <div className="relative z-0 mt-10 h-32 w-full sm:h-36 md:h-40 lg:h-44">
        <div className="absolute inset-0 z-10 bg-gradient-to-t from-[#100d16] from-35% to-transparent" />
        <div className="absolute inset-0 mx-4 sm:mx-6">
          <FlickeringGrid
            text={t('footer.brandName')}
            fontSize={narrow ? 32 : tablet ? 44 : 56}
            className="h-full w-full"
            squareSize={2}
            gridGap={2}
            color="#94a3b8"
            maxOpacity={0.28}
            flickerChance={0.1}
          />
        </div>
      </div>

      <div className="mx-auto flex max-w-[1440px] flex-col items-center justify-between gap-4 border-t border-[#4a4455]/60 px-6 py-8 md:flex-row sm:px-12">
        <p className="text-xs text-white/50">{t('footer.copyright', { year: new Date().getFullYear() })}</p>
        <nav className="flex flex-wrap items-center justify-center gap-2 md:justify-end" aria-label="Social media">
          {socialLinks.map(({ key, href }) => (
            <a
              key={key}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t(`footer.social.${key}`)}
              className="group flex size-10 items-center justify-center rounded-none border border-white/15 bg-white/[0.03] text-white/55 transition-colors hover:border-[#7c3aed]/60 hover:bg-[#7c3aed]/10 hover:text-[#d2bbff]"
            >
              {key === 'linkedin' && <IconLinkedIn className="size-[18px] text-current" />}
              {key === 'instagram' && <IconInstagram className="size-[18px] text-current" />}
            </a>
          ))}
        </nav>
      </div>
    </footer>
  )
}

export function Highlight({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span className={cn('rounded-none bg-sky-500/15 p-1 py-0.5 font-medium text-sky-300', className)}>{children}</span>
  )
}
